import { request, getApiUrl } from '@/utils/ajax';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { getUniqueId } from '@/utils/html';
import {
    DocumentStatus,
    ResidentialUnitDocument,
    ResidentialUnitDocumentVersion,
    ProjectDocument,
    DocumentLink,
    ProjectDocumentVersion,
    SimpleDocument,
    BaseDocument,
    ResidentialUnit,
} from '@/types';

export interface ResidentialUnitDocumentSearchOptions {
    projectId?: number | null;
    residentialUnitIds?: number[];
    status?: DocumentStatus[];
    categories?: number[];
    approvableOnly?: boolean;
    excludeProjectDocuments?: boolean;
    includeArchived?: boolean;
    includeUnpublishedVersionsCount?: boolean;
}

export function searchResidentialUnitDocuments(
    options: ResidentialUnitDocumentSearchOptions
): Promise<ResidentialUnitDocument[]> {
    return request(`api/document`, { query: options }).then((x) => x.json());
}

export interface ProjectDocumentSearchOptions {
    projectId: number;
    categories?: number[];
    status?: DocumentStatus[];
    approvableOnly?: boolean;
    includeArchived?: boolean;
    IncludeUnpublishedVersionsCount?: boolean;
}

export function searchProjectDocuments(options: ProjectDocumentSearchOptions): Promise<ProjectDocument[]> {
    return request(`api/project/${options.projectId}/documents`, { query: options }).then((x) => x.json());
}

export function getResidentialUnitDocuments(residentialUnitId: number): Promise<ResidentialUnitDocument[]> {
    return request(`api/residential-unit/${residentialUnitId}/documents`).then((x) => x.json());
}

export function getResidentialUnitDocumentVersions(
    documentId: string,
    residentialUnitId: number
): Promise<ResidentialUnitDocumentVersion[]> {
    return request(`api/document/${documentId}/residential-unit/${residentialUnitId}/versions`).then((x) => x.json());
}

export function getProjectDocumentVersions(documentId: string): Promise<ProjectDocumentVersion[]> {
    return request(`api/document/${documentId}/versions`).then((x) => x.json());
}

export interface UploadedDocumentVersion {
    versionId?: number;
    documentId: string;
    floor: number;
    documentTypeId: number | null;
    name: string;
    isSensitive: boolean;
    ownerProfileId: number;

    fileData: FileData | null;
    croppedImageFileData: FileData | null;
    hideFromBuyer: boolean;
}

export function addResidentialUnitDocument(residentialUnitId: number, document: UploadedDocumentVersion) {
    return request(`api/residential-unit/${residentialUnitId}/documents`, {
        method: 'POST',
        json: true,
        body: document,
    }).then((x) => x.json());
}

export function addProjectDocument(projectId: number, document: UploadedDocumentVersion) {
    return request(`api/project/${projectId}/documents`, {
        method: 'POST',
        json: true,
        body: document,
    }).then((x) => x.json());
}

export function renameDocument(documentVersionId: number, newName: string) {
    return request(`api/document/${documentVersionId}/name`, {
        method: 'PATCH',
        json: true,
        body: { name: newName },
    });
}

// export function updateResidentialUnitDocument(documentId, document) {
//     return request(`api/document/${documentId}`, {
//         method: 'PUT',
//         json: true,
//         body: document,
//     });
// }

export function deleteResidentialUnitDocument(documentId: string) {
    return request(`api/document/${documentId}`, {
        method: 'DELETE',
    });
}

export function getDownloadDocumentUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/download`;
}

export function getOpenDocumentUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/open`;
}

export function getDocumentThumbnailUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/thumbnail`;
}

export function getDownloadOriginalDocumentUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/original/download`;
}

export function getOpenOriginalDocumentUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/original/open`;
}

export function getResidentialUnitFloorPlanUrl(residentialUnitId: number) {
    return getApiUrl() + `/api/residential-unit/${residentialUnitId}/floor-plan/open`;
}

export function getDocumentLinkedResidentialUnits(versionId: number): Promise<DocumentLink[]> {
    return request(`api/document/${versionId}/links`, {
        method: 'GET',
    }).then((x) => x.json());
}

export function setDocumentLinkedResidentialUnits(versionId: number, residentialUnitIds: number[]) {
    return request(`api/document/${versionId}/links`, {
        method: 'POST',
        json: true,
        body: {
            residentialUnitIds,
        },
    });
}

export function approveResidentialUnitDocument(
    versionId: number,
    residentialUnitIds: number[],
    sendEmailNotification: boolean,
    emailMessage?: string
) {
    return request(`api/document/${versionId}/approve`, {
        method: 'POST',
        json: true,
        body: {
            residentialUnitIds,
            sendEmailNotification,
            emailMessage,
        },
    });
}

export enum BulkDocumentActionMode {
    AllUnpublished = 0,
    LatestUnpublished = 1,
}
export interface DocumentActionItem {
    documentId: BaseDocument['documentId'];
    residentialUnitIds: ResidentialUnit['id'][];
}

export interface DocumentApprovalRequest {
    docsToApprove: DocumentActionItem[];
    sendEmail: boolean;
    emailMessage: string;
    approvalMode: BulkDocumentActionMode;
}

export function bulkApproveResidentialUnitDocument({
    docsToApprove,
    sendEmail,
    emailMessage,
    approvalMode,
}: DocumentApprovalRequest) {
    return request('/api/document/bulk-approve', {
        method: 'POST',
        json: true,
        body: {
            documents: docsToApprove,
            sendEmailNotification: sendEmail,
            emailMessage,
            approvalMode,
        },
    });
}

interface DocumentRejectionRequest {
    documents: Array<DocumentActionItem>;
    reason: string;
    sendRejectEmail: boolean;
}

export function bulkRejectResidentialUnitDocument({ documents, reason, sendRejectEmail }: DocumentRejectionRequest) {
    return request('/api/document/bulk-reject', {
        method: 'POST',
        json: true,
        body: {
            documents,
            reason,
            sendRejectEmail,
        },
    });
}

export function rejectResidentialUnitDocument(
    versionId: number,
    residentialUnitIds: number[],
    reason: string,
    sendRejectEmail: boolean
) {
    return request(`api/document/${versionId}/reject`, {
        method: 'POST',
        json: true,
        body: {
            residentialUnitIds,
            reason,
            sendRejectEmail,
        },
    });
}

export function getDocumentFileType(document) {
    if (document.fileData) {
        return document.fileData.getFileType();
    }

    return document.fileType;
}

export function getResidentialUnitDocumentTypes(projectId: number) {
    return request(`api/project/${projectId}/document-types`).then((x) => x.json());
}

// export const getResidentialUnitDocumentTypes = _memoize(function() {
//     return request(`api/document/types`).then((x) => x.json());
// });

export function pdfToImage(fileData: FileData) {
    var data = new FormData();
    data.append(getUniqueId(), fileData.originalFile);

    return request(`https://pdf-converter.bygr.no/upload`, {
        method: 'POST',
        body: data,
        credentials: 'omit',
    })
        .then((x) => x.blob())
        .then((x) => new FileData(x, fileData.fileName.replace(/\.pdf$/i, '.jpeg')));
}

export function setDocumentHiddenFromBuyer(versionId: number, isHiddenFromBuyer: boolean) {
    return request(`api/document/${versionId}/hidden-from-buyer`, {
        method: 'PATCH',
        json: true,
        body: {
            isHiddenFromBuyer,
        },
    });
}

export function setDocumentIsSensitive(versionId: number, isSensitive: boolean) {
    return request(`api/document/${versionId}/sensitive`, {
        method: 'PATCH',
        json: true,
        body: {
            isSensitive,
        },
    });
}

export function setDocumentIsArchived(versionId: number, isArchived: boolean) {
    return request(`api/document/${versionId}/archived`, {
        method: 'PATCH',
        json: true,
        body: {
            isArchived,
        },
    });
}

export function getOpenDocumentModelUrl(versionId: number) {
    return getApiUrl() + `/api/document/${versionId}/model`;
}

export function getDocument(documentId: string): Promise<SimpleDocument> {
    return request(`api/document/${documentId}`).then((x) => x.json());
}

export function zipResidentialUnitDocuments(options: ResidentialUnitDocumentSearchOptions) {
    return request(`api/document/zip-residential-unit-documents/`, {
        method: 'POST',
        query: options,
    });
}

export function getZippableResidentialUnitDocuments(
    options: ResidentialUnitDocumentSearchOptions
): Promise<ResidentialUnitDocument[]> {
    return request(`api/document/zippable-residential-unit-documents/`, {
        method: 'GET',
        query: options,
    }).then((x) => x.json());
}

export function zipProjectDocuments(options: ProjectDocumentSearchOptions) {
    return request(`api/project/${options.projectId}/zip-documents`, { query: options, method: 'POST' });
}

export function getZippableProjectDocuments(options: ProjectDocumentSearchOptions): Promise<ProjectDocument[]> {
    return request(`api/project/${options.projectId}/zippable-documents`, { query: options, method: 'GET' }).then((x) =>
        x.json()
    );
}
export function getZipDownloadUrl(zipId: string) {
    return getApiUrl() + `/api/zip/${zipId}`;
}
